import { useState, useEffect } from 'react';
import { ref as dbRef, get } from 'firebase/database';
import { database } from '../config/firebase';
import { useTestMode } from './useTestMode';

/**
 * useCompanyBySlug — Fase 5 del piano.
 *
 * Risolve lo slug pubblico (es. "lg-gold-rent" da /lg-gold-rent) in un companyId,
 * tramite il nodo slugs/{slug} (lookup O(1), non serve scansionare tutte le company).
 *
 * Restituisce anche subscriptionStatus e stripeConnected, necessari al sito
 * pubblico per decidere cosa mostrare:
 *   - notFound: lo slug non esiste -> pagina "azienda non trovata"
 *   - subscriptionStatus 'expired'|'cancelled' -> sito bloccato (Fase 8/9)
 *   - accountBlocked true -> sito bloccato (sospensione manuale platform admin,
 *     indipendente dal ciclo di vita abbonamento — vedi setCompanyBlocked)
 *   - stripeConnected false -> catalogo visibile ma prenotazioni disabilitate
 *
 * FIX (Agosto 2026): stripeConnected veniva letto sempre da
 * `companies/{id}/stripeConnected` (il campo LIVE), a prescindere dal fatto
 * che la piattaforma fosse in modalità test. Risultato: con un account
 * collegato in live ma non in test, il sito pubblico mostrava le
 * prenotazioni come attive anche in test mode, lasciando arrivare il
 * cliente fino al pagamento — dove createPaymentIntent (che legge
 * correttamente stripeConnectAccountIdTest) falliva. Ora il campo letto
 * segue la stessa convenzione del backend (stripeFieldPath in
 * functions/index.js): stripeConnected in live, stripeConnectedTest in test.
 */
export function useCompanyBySlug(slug) {
  const { testMode, loaded: testModeLoaded } = useTestMode();

  const [state, setState] = useState({
    companyId: null,
    ragioneSociale: null,
    subscriptionStatus: null,
    accountBlocked: false,
    stripeConnected: false,
    loading: true,
    notFound: false,
  });

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      if (!slug) {
        if (!cancelled) setState(s => ({ ...s, loading: false, notFound: true }));
        return;
      }

      // Aspetta di sapere se la piattaforma è in test mode prima di decidere
      // quale campo Stripe leggere, altrimenti rischieremmo di leggere il
      // campo live per un istante e "sbiancare" il banner in modo scorretto.
      if (!testModeLoaded) return;

      try {
        const slugSnap = await get(dbRef(database, `slugs/${slug}`));
        if (!slugSnap.exists()) {
          if (!cancelled) setState(s => ({ ...s, loading: false, notFound: true }));
          return;
        }

        const companyId = slugSnap.val();
        const stripeConnectedField = testMode ? 'stripeConnectedTest' : 'stripeConnected';

        // FIX (29/09/2026): database.rules.json consente al visitatore anonimo di leggere
        // SOLO i tre campi pubblici di info/ (ragioneSociale, subscriptionStatus,
        // accountBlocked), non il nodo info/ intero (riservato ad admin e platform admin,
        // perché contiene piva, telefono, id Stripe...). Leggere il nodo padre veniva
        // rifiutato con PERMISSION_DENIED e il sito pubblico mostrava "azienda non trovata".
        // Quindi si leggono i tre campi singolarmente (in parallelo).
        const infoField = (name) => dbRef(database, `companies/${companyId}/info/${name}`);
        const [ragioneSocialeSnap, statusSnap, blockedSnap, stripeConnectedSnap] = await Promise.all([
          get(infoField('ragioneSociale')),
          get(infoField('subscriptionStatus')),
          get(infoField('accountBlocked')),
          get(dbRef(database, `companies/${companyId}/${stripeConnectedField}`)),
        ]);

        if (cancelled) return;

        // Stessa semantica di prima: se di info/ non esiste nessuno dei campi pubblici
        // l'azienda è considerata inesistente (registerCompany scrive sempre almeno
        // ragioneSociale e subscriptionStatus).
        if (!ragioneSocialeSnap.exists() && !statusSnap.exists() && !blockedSnap.exists()) {
          setState(s => ({ ...s, loading: false, notFound: true }));
          return;
        }

        setState({
          companyId,
          ragioneSociale: ragioneSocialeSnap.val() || slug,
          subscriptionStatus: statusSnap.val() || null,
          accountBlocked: blockedSnap.val() === true,
          stripeConnected: stripeConnectedSnap.exists() ? Boolean(stripeConnectedSnap.val()) : false,
          loading: false,
          notFound: false,
        });
      } catch (error) {
        console.error('Errore risoluzione company da slug:', error);
        if (!cancelled) setState(s => ({ ...s, loading: false, notFound: true }));
      }
    }

    resolve();
    return () => { cancelled = true; };
  }, [slug, testMode, testModeLoaded]);

  return state;
}
